import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Spinner,
  Badge,
  InputGroup,
  Modal,
} from "react-bootstrap";
import Pagination from "../components/Pagination";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getSkorTickets,
  getSkorTicketById,
  updateSkorTicketStatus,
  adminLogin,
  getTenantDomains,
} from "../context/functions/AdminFunction";
import { Ucontext } from "../context/Ucontext";
import { REACT_APP_URL } from "../config";
import { constants, SKOR_TICKETS_CHANGED_EVENT } from "../utils/constants";
import {
  FiSearch,
  FiX,
  FiRefreshCw,
  FiChevronUp,
  FiChevronDown,
  FiInfo,
  FiPaperclip,
  FiUser,
  FiLogIn,
} from "react-icons/fi";
import "../styles/llm-cost-analysis.css";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Open", label: "Open" },
  { value: "In progress", label: "In progress" },
  { value: "Waiting on user", label: "Waiting on user" },
  { value: "Resolved", label: "Resolved" },
  { value: "Closed", label: "Closed" },
];

const URGENCY_OPTIONS = [
  { value: "", label: "All Urgency" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "document_issue", label: "Document issue" },
  { value: "missing_doc_note", label: "Missing-doc note" },
  { value: "missing_documents", label: "Missing documents" },
  { value: "document_classification", label: "Classification of documents" },
  { value: "information_extraction", label: "Information Extraction" },
  { value: "other_profile_issues", label: "Other profile issues" },
  { value: "fraud", label: "Fraud" },
  { value: "score", label: "Score" },
  { value: "bug", label: "Bug" },
  { value: "account_access", label: "Account access" },
  { value: "billing", label: "Billing" },
  { value: "integration_api", label: "Integration / API" },
  { value: "product_question", label: "Product question" },
  { value: "feature_request", label: "Feature request" },
  { value: "other", label: "Other" },
];

const LINKED_TYPE_OPTIONS = [
  { value: "", label: "All linked types" },
  { value: "Tenant", label: "Tenant" },
  { value: "Application", label: "Application" },
  { value: "Property", label: "Property" },
  { value: "Tenant,Application,Property", label: "Any linked" },
];

const CREATOR_OPTIONS = [
  { value: "", label: "All Creators" },
  { value: "Tenant", label: "Tenant" },
  { value: "LandLord", label: "Landlord" },
];

const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "missing_doc_note", label: "Missing docs issue" },
];

const CATEGORY_LABEL = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label])
);

const STATUS_BADGE = {
  Open: "primary",
  "In progress": "warning",
  "Waiting on user": "info",
  Resolved: "success",
  Closed: "secondary",
};

const URGENCY_BADGE = {
  high: "danger",
  medium: "warning",
  low: "success",
};

const creatorName = (ticket) => {
  const c = ticket.creator_id;
  if (!c || typeof c !== "object") return "—";
  if (ticket.creator_type === "LandLord") {
    return c.fname_companyName || c.email || "—";
  }
  const full = `${c.fname || ""} ${c.lname || ""}`.trim();
  return full || c.email || "—";
};

const creatorContact = (ticket) => {
  const c = ticket.creator_id;
  if (!c || typeof c !== "object") return "—";
  return c.email || c.phone || c.phone_number || "—";
};

const ticketType = (ticket) => {
  const links = Array.isArray(ticket?.links) ? ticket.links : [];
  if (!ticket?.tenant_id && links.length === 0) return "General";
  return ticket.creator_type === "LandLord" ? "Landlord" : "Tenant";
};

const getTicketLinks = (ticket) =>
  Array.isArray(ticket?.links) ? ticket.links : [];

const getLinkLabel = (link) => {
  const target = link?.linked_id;
  if (!target || typeof target !== "object") {
    const raw = typeof target === "string" ? target : "";
    return raw ? `…${raw.slice(-6)}` : "—";
  }
  if (link.linked_type === "Tenant") {
    const full = `${target.fname || ""} ${target.lname || ""}`.trim();
    return full || target.forensic_name || target.email || "Profile";
  }
  if (link.linked_type === "Application") {
    return target.title || "Application";
  }
  // Property
  return target.address || target.property_reference || "Property";
};

const getLinkSubtitle = (link) => {
  const target = link?.linked_id;
  if (!target || typeof target !== "object") {
    return typeof target === "string" ? target : "";
  }
  if (link.linked_type === "Tenant") return target.email || "";
  if (link.linked_type === "Application") return "Application";
  return target.property_reference || "Property";
};

const tenantName = (tenant) => {
  if (!tenant || typeof tenant !== "object") return "—";
  const full = `${tenant.fname || ""} ${tenant.lname || ""}`.trim();
  return full || tenant.email || "—";
};

const getRawId = (value) =>
  value && typeof value === "object" ? value._id : value;

const collectTenantIds = (ticket) => {
  const ids = [];
  const links = Array.isArray(ticket?.links) ? ticket.links : [];
  links.forEach((l) => {
    if (l?.linked_type !== "Tenant") return;
    const id = getRawId(l?.linked_id);
    if (id && !ids.includes(String(id))) ids.push(String(id));
  });
  const legacyId = getRawId(ticket?.tenant_id);
  if (legacyId && !ids.includes(String(legacyId))) ids.push(String(legacyId));
  return ids;
};

const SortHeader = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active ? (
          sortOrder === "desc" ? (
            <FiChevronDown size={12} />
          ) : (
            <FiChevronUp size={12} />
          )
        ) : (
          <FiChevronDown size={12} style={{ opacity: 0.3 }} />
        )}
      </span>
    </th>
  );
};

const SkorTickets = () => {
  const { authState } = useContext(Ucontext);
  const isSuperAdmin = authState?.user?.role === constants.SUPER_ADMIN;
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("");
  const [creatorType, setCreatorType] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [linkedType, setLinkedType] = useState("");
  const [linkedId, setLinkedId] = useState("");
  const [generalOnly, setGeneralOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Detail modal
  const [showModal, setShowModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editUrgency, setEditUrgency] = useState("");
  const [saving, setSaving] = useState(false);
  // tenantId -> { loading, landlords: [{ landlordId, landlordName, email, workspaceName }] }
  const [sharedLandlords, setSharedLandlords] = useState({});
  const [loginBusy, setLoginBusy] = useState(null);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    document.title = "Skor Tickets | Admin";
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const fetchData = useCallback(
    async (currentPage = 1, signal = null) => {
      setLoading(true);
      try {
        const filters = {
          search: debouncedSearch,
          status,
          // The Type select is a shortcut for the missing-docs category and
          // takes precedence over the Category select when set.
          category: typeFilter || category,
          urgency,
          creator_type: creatorType,
          linked_type: generalOnly ? "" : linkedType,
          linked_id: generalOnly ? "" : linkedId.trim(),
          general: generalOnly ? "true" : "",
          date_from: dateFrom,
          date_to: dateTo,
          sortBy,
          sortOrder,
        };
        const res = await getSkorTickets(filters, currentPage, limit, signal);
        if (res?.status === "canceled") return;
        if (res && res.success) {
          setTickets(res.data.tickets || []);
          setStats(res.data.stats || null);
          setTotalPages(res.data.pagination?.totalPages || 1);
          setTotalRecords(res.data.pagination?.totalRecords || 0);
        } else {
          setTickets([]);
          setStats(null);
          setTotalPages(1);
          setTotalRecords(0);
        }
      } catch (e) {
        console.error(e);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, status, category, typeFilter, urgency, creatorType, linkedType, linkedId, generalOnly, dateFrom, dateTo, sortBy, sortOrder]
  );

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchData(page, controller.signal);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchData, page]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handlePageClick = (e) => setPage(e.selected + 1);

  const handleReset = () => {
    setSearch("");
    setStatus("");
    setCategory("");
    setUrgency("");
    setCreatorType("");
    setTypeFilter("");
    setLinkedType("");
    setLinkedId("");
    setGeneralOnly(false);
    setDateFrom("");
    setDateTo("");
    setSortBy("created_at");
    setSortOrder("desc");
    setPage(1);
  };

  const openDetail = async (ticket) => {
    setShowModal(true);
    setDetail(null);
    setSharedLandlords({});
    setDetailLoading(true);
    const res = await getSkorTicketById(ticket._id);
    setDetailLoading(false);
    if (res?.success) {
      setDetail(res.data.ticket);
      setEditStatus(res.data.ticket.status || "Open");
      setEditUrgency(res.data.ticket.urgency || "medium");
    } else {
      toast.error("Failed to load ticket");
    }
  };

  // Fetch the landlords each linked tenant is shared with (same source as
  // the Tenants page "View" workspaces modal).
  useEffect(() => {
    if (!detail || !isSuperAdmin) return;
    const ids = collectTenantIds(detail).filter(
      (id) => sharedLandlords[id] === undefined
    );
    if (ids.length === 0) return;
    ids.forEach(async (tenantId) => {
      setSharedLandlords((prev) => ({
        ...prev,
        [tenantId]: { loading: true, landlords: [] },
      }));
      try {
        const res = await getTenantDomains(tenantId);
        const workspaces = res?.data ?? [];
        const seen = new Set();
        const landlords = [];
        workspaces.forEach((w) => {
          (w?.landlords || []).forEach((l) => {
            const key = String(l?.landlordId || l?._id || l?.email || "");
            if (!key || seen.has(key)) return;
            seen.add(key);
            landlords.push({
              key,
              landlordId: l?.landlordId || l?._id,
              landlordName: l?.landlordName || "—",
              email: l?.email || "",
              workspaceName: w?.workspaceName || "",
            });
          });
        });
        setSharedLandlords((prev) => ({
          ...prev,
          [tenantId]: { loading: false, landlords },
        }));
      } catch (err) {
        console.error(err);
        setSharedLandlords((prev) => ({
          ...prev,
          [tenantId]: { loading: false, landlords: [] },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, isSuperAdmin]);

  const openImpersonation = (res, userType) => {
    const queryParams = new URLSearchParams({
      token: res?.token || "",
      usertype: userType || "",
      is_verified: res?.user?.is_verified || true,
      saveduserid: res?.user?._id || "",
      external_customer_id: res?.user?._id || "",
      app_type: "skor",
    });
    window.open(
      new URL(`${REACT_APP_URL}/redirect?${queryParams.toString()}`).href,
      "_blank"
    );
  };

  const handleLoginAsTenant = async (tenantId) => {
    if (!tenantId || loginBusy) return;
    setLoginBusy(`tenant:${tenantId}`);
    try {
      const res = await adminLogin({ user_id: tenantId, user_type: "tenant" });
      if (res?.token) openImpersonation(res, "tenant");
      else toast.error("Login as tenant failed");
    } catch (err) {
      console.error(err);
      toast.error("Login as tenant failed");
    } finally {
      setLoginBusy(null);
    }
  };

  const handleLoginAsLandlord = async (landlordId) => {
    if (!landlordId || loginBusy) return;
    setLoginBusy(`landlord:${landlordId}`);
    try {
      const res = await adminLogin({
        user_id: landlordId,
        user_type: "landlord",
      });
      if (res?.token) openImpersonation(res, "landlord");
      else toast.error("Login as landlord failed");
    } catch (err) {
      console.error(err);
      toast.error("Login as landlord failed");
    } finally {
      setLoginBusy(null);
    }
  };

  const handleSaveStatus = async () => {
    if (!detail?._id || !editStatus) return;
    const statusChanged = editStatus !== detail.status;
    const urgencyChanged = editUrgency && editUrgency !== detail.urgency;
    if (!statusChanged && !urgencyChanged) return;
    setSaving(true);
    try {
      const res = await updateSkorTicketStatus(detail._id, {
        status: editStatus,
        urgency: editUrgency,
      });
      if (res?.success) {
        toast.success("Ticket updated");
        setShowModal(false);
        fetchData(page);
        // Tell the sidebar to refetch its open-ticket badge immediately.
        // (No-op if status/urgency didn't actually change — we return early above.)
        window.dispatchEvent(new Event(SKOR_TICKETS_CHANGED_EVENT));
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const badge = (value, map) => (
    <Badge bg={map[value] || "secondary"} style={{ fontSize: 12, fontWeight: 700 }}>
      {value || "—"}
    </Badge>
  );

  return (
    <div className="llm-cost-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4
            className="fw-800 mb-1"
            style={{ color: "var(--llm-color-text-primary)", fontSize: "22px" }}
          >
            Skor Tickets
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Support requests opened by tenants and landlords.
          </p>
        </div>
        <Button
          variant="white"
          className="border shadow-none d-flex align-items-center gap-2"
          style={{ fontSize: "13px", fontWeight: 700, borderRadius: "8px" }}
          onClick={() => fetchData(page)}
        >
          <FiRefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="d-flex gap-3 mb-4 flex-wrap">
          {[
            { label: "Open", value: stats.open, color: "#3b82f6" },
            { label: "In progress", value: stats.in_progress, color: "#f59e0b" },
            { label: "Waiting", value: stats.waiting_on_user, color: "#06b6d4" },
            { label: "Resolved", value: stats.resolved, color: "#22c55e" },
            { label: "Closed", value: stats.closed, color: "#94a3b8" },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: "#fff",
                border: "1px solid #e8edf2",
                borderRadius: 12,
                padding: "14px 20px",
                flex: 1,
                minWidth: 120,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                {s.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>
                {(s.value || 0).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Body className="p-3">
          <div className="llm-filter-row skor-filter-row">
            <div className="llm-filter-item skor-filter-search">
              <span className="llm-filter-label">Search</span>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0">
                  <FiSearch size={13} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  className="border-start-0 ps-1"
                  style={{ height: "38px" }}
                  type="text"
                  placeholder="Subject, message or document..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <InputGroup.Text
                    className="bg-white border-start-0"
                    onClick={() => setSearch("")}
                    style={{ cursor: "pointer" }}
                  >
                    <FiX size={13} className="text-danger" />
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>

            {[
              { label: "Status", val: status, set: setStatus, opts: STATUS_OPTIONS },
              { label: "Category", val: category, set: setCategory, opts: CATEGORY_OPTIONS, disabled: !!typeFilter },
              { label: "Urgency", val: urgency, set: setUrgency, opts: URGENCY_OPTIONS },
              { label: "Creator", val: creatorType, set: setCreatorType, opts: CREATOR_OPTIONS },
            ].map((f) => (
              <div className="llm-filter-item skor-span-2" key={f.label}>
                <span className="llm-filter-label">{f.label}</span>
                <Form.Select
                  size="sm"
                  value={f.val}
                  disabled={f.disabled}
                  onChange={(e) => {
                    f.set(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: "38px", borderRadius: "6px" }}
                >
                  {f.opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            ))}

            <div className="llm-filter-item skor-span-2">
              <span className="llm-filter-label">Type</span>
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                style={{ height: "38px", borderRadius: "6px" }}
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="llm-filter-item skor-span-2">
              <span className="llm-filter-label">Linked type</span>
              <Form.Select
                size="sm"
                value={linkedType}
                disabled={generalOnly}
                onChange={(e) => {
                  setLinkedType(e.target.value);
                  if (e.target.value) setGeneralOnly(false);
                  setPage(1);
                }}
                style={{ height: "38px", borderRadius: "6px" }}
              >
                {LINKED_TYPE_OPTIONS.map((o) => (
                  <option key={o.label} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="llm-filter-item skor-span-3">
              <span className="llm-filter-label">Linked IDs</span>
              <Form.Control
                size="sm"
                type="text"
                placeholder="ObjectIds, comma-separated..."
                value={linkedId}
                disabled={generalOnly}
                onChange={(e) => {
                  setLinkedId(e.target.value);
                  if (e.target.value.trim()) setGeneralOnly(false);
                  setPage(1);
                }}
                style={{ height: "38px", borderRadius: "6px" }}
              />
            </div>

            <div className="llm-filter-item skor-span-3">
              <span className="llm-filter-label">From</span>
              <Form.Control
                size="sm"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                style={{ height: "38px", borderRadius: "6px" }}
              />
            </div>
            <div className="llm-filter-item skor-span-3">
              <span className="llm-filter-label">To</span>
              <Form.Control
                size="sm"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                style={{ height: "38px", borderRadius: "6px" }}
              />
            </div>

            <div className="llm-filter-item skor-span-2">
              <span className="llm-filter-label">General</span>
              <Form.Check
                type="switch"
                id="skor-general-toggle"
                checked={generalOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setGeneralOnly(checked);
                  if (checked) {
                    setLinkedType("");
                    setLinkedId("");
                  }
                  setPage(1);
                }}
                style={{ height: "38px", display: "flex", alignItems: "center", fontSize: "13px", fontWeight: 600 }}
              />
            </div>

            <Button
              className="llm-btn-apply skor-span-3"
              onClick={handleReset}
              style={{ background: "#f1f5f9", color: "#253837", border: "1px solid #e2e8f0" }}
            >
              Reset
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
          <h6
            className="mb-0 fw-800 text-uppercase"
            style={{ fontSize: "13px", letterSpacing: "0.05em" }}
          >
            Tickets
          </h6>
          <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>
            {totalRecords.toLocaleString()} total records
          </span>
        </Card.Header>
        <div className="table-responsive">
          <Table className="llm-table" hover>
            <thead>
              <tr>
                <SortHeader label="Created" field="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th>Subject</th>
                <th>Category</th>
                <th>Urgency</th>
                <th>Status</th>
                <th>Creator</th>
                <th className="text-center">Files</th>
                <th className="text-center">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-5">
                    <Spinner size="sm" animation="border" variant="primary" />
                    <p className="mt-2 text-muted small fw-bold text-uppercase mb-0">
                      Loading...
                    </p>
                  </td>
                </tr>
              ) : tickets.length > 0 ? (
                tickets.map((t) => (
                  <tr key={t._id} style={{ cursor: "pointer" }} onClick={() => openDetail(t)}>
                    <td>
                      <span className="fw-bold d-block text-dark" style={{ fontSize: "14px" }}>
                        {moment(t.created_at).format("DD MMM YYYY")}
                      </span>
                      <small className="text-muted fw-medium" style={{ fontSize: "12px" }}>
                        {moment(t.created_at).format("HH:mm")}
                      </small>
                    </td>
                    <td style={{ maxWidth: 280 }}>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#334155",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {t.subject || "—"}
                      </span>
                      {t.message && (
                        <span
                          className="d-block"
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: "#475569",
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            marginTop: 2,
                          }}
                        >
                          {t.message}
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: 13, color: "#475569" }}>
                        {CATEGORY_LABEL[t.category] || t.category || "—"}
                      </span>
                    </td>
                    <td>{badge(t.urgency, URGENCY_BADGE)}</td>
                    <td>{badge(t.status, STATUS_BADGE)}</td>
                    <td>
                      <div style={{ fontSize: "13px" }}>
                        <div className="fw-bold text-dark d-flex align-items-center gap-1">
                          <FiUser size={11} className="text-muted" /> {creatorName(t)}
                        </div>
                        <div className="text-muted">{creatorContact(t)}</div>
                        <small className="text-muted fw-medium" style={{ fontSize: "12px" }}>
                          {ticketType(t)}
                          {t.workspace_id?.name ? ` · ${t.workspace_id.name}` : ""}
                        </small>
                      </div>
                    </td>
                    <td className="text-center fw-700" style={{ color: (t.attachments?.length || 0) > 0 ? "#2563eb" : "#94a3b8" }}>
                      <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                        <FiPaperclip size={12} /> {t.attachments?.length || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="white"
                        className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1"
                        style={{ fontSize: "12px", borderRadius: "6px", fontWeight: 700 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(t);
                        }}
                      >
                        <FiInfo size={13} /> View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <FiSearch size={32} className="mb-2 opacity-25 d-block mx-auto" />
                    <span className="fw-bold">No tickets found</span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {!loading && tickets.length > 0 && (
          <div className="d-flex justify-content-end p-3 border-top bg-light bg-opacity-50">
            <Pagination
              currentPage={page - 1}
              totalPages={totalPages}
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              className="pagination mb-0"
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="fw-bold h5">
            {detail?.subject || "Ticket"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {detailLoading || !detail ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <div>
              {/* Status + badges */}
              <div className="d-flex gap-2 mb-3 flex-wrap">
                {badge(detail.status, STATUS_BADGE)}
                {badge(detail.urgency, URGENCY_BADGE)}
                <Badge bg="light" text="dark" className="border" style={{ fontSize: 11 }}>
                  {CATEGORY_LABEL[detail.category] || detail.category || "—"}
                </Badge>
              </div>

              {/* Info - flat rows, no boxes */}
              <div className="mb-3" style={{ borderTop: "1px solid #eef2f7" }}>
                <div className="d-flex gap-3 py-2" style={{ borderBottom: "1px solid #eef2f7" }}>
                  <span className="text-muted fw-bold text-uppercase flex-shrink-0" style={{ fontSize: "10px", width: 90, paddingTop: 2 }}>
                    Creator
                  </span>
                  <span style={{ fontSize: "13px" }} className="flex-grow-1">
                    <span className="fw-bold text-dark">{creatorName(detail)}</span>
                    <span className="text-muted"> · {creatorContact(detail)}</span>
                  </span>
                  {isSuperAdmin && detail.creator_type === "LandLord" && getRawId(detail.creator_id) && (
                    <Button
                      variant="white"
                      className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1 flex-shrink-0 align-self-center"
                      style={{ fontSize: "12px", borderRadius: "6px", fontWeight: 700 }}
                      disabled={loginBusy === `landlord:${getRawId(detail.creator_id)}`}
                      onClick={() => handleLoginAsLandlord(String(getRawId(detail.creator_id)))}
                      title="Login as landlord"
                    >
                      <FiLogIn size={13} /> {loginBusy === `landlord:${getRawId(detail.creator_id)}` ? "..." : "Login"}
                    </Button>
                  )}
                </div>
                <div className="d-flex gap-3 py-2" style={{ borderBottom: "1px solid #eef2f7" }}>
                  <span className="text-muted fw-bold text-uppercase flex-shrink-0" style={{ fontSize: "10px", width: 90, paddingTop: 2 }}>
                    Context
                  </span>
                  <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                    {ticketType(detail)}
                    {detail.workspace_id?.name ? <span className="text-muted fw-normal"> · {detail.workspace_id.name}</span> : null}
                  </span>
                </div>
                <div className="d-flex gap-3 py-2" style={{ borderBottom: "1px solid #eef2f7" }}>
                  <span className="text-muted fw-bold text-uppercase flex-shrink-0" style={{ fontSize: "10px", width: 90, paddingTop: 2 }}>
                    Timeline
                  </span>
                  <span style={{ fontSize: "13px" }} className="text-dark">
                    <span>Created {moment(detail.created_at).format("DD MMM YYYY, HH:mm")}</span>
                    <span className="text-muted"> · Updated {moment(detail.updated_at).format("DD MMM YYYY, HH:mm")}</span>
                    {detail.resolved_at && (
                      <span> · Resolved {moment(detail.resolved_at).format("DD MMM YYYY, HH:mm")}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Related records (flexible links) */}
              <div className="mb-3">
                <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                  Related records ({(() => {
                    const base = getTicketLinks(detail).length;
                    const legacyTenantId = getRawId(detail.tenant_id);
                    const hasTenantLink = getTicketLinks(detail).some((l) => l.linked_type === "Tenant");
                    return base + (!hasTenantLink && legacyTenantId ? 1 : 0);
                  })()})
                </p>
                {(() => {
                  const baseLinks = getTicketLinks(detail);
                  const legacyTenantId = getRawId(detail.tenant_id);
                  const hasTenantLink = baseLinks.some((l) => l.linked_type === "Tenant");
                  const effectiveLinks =
                    !hasTenantLink && legacyTenantId
                      ? [...baseLinks, { linked_type: "Tenant", linked_id: detail.tenant_id }]
                      : baseLinks;
                  if (effectiveLinks.length === 0) {
                    return (
                      <div className="text-muted small">
                        No linked records.
                      </div>
                    );
                  }
                  return (
                  <div style={{ borderTop: "1px solid #eef2f7" }}>
                    {["Tenant", "Application", "Property"].map((linkType) => {
                      const group = effectiveLinks.filter((l) => l.linked_type === linkType);
                      if (group.length === 0) return null;
                      return (
                        <div key={linkType}>
                          <div className="d-flex flex-column">
                            {group.map((link, i) => {
                              const rawId = getRawId(link.linked_id);
                              const tenantEntry = linkType === "Tenant" && rawId ? sharedLandlords[String(rawId)] : null;
                              return (
                                <div key={`${linkType}-${rawId}-${i}`} className="py-2" style={{ borderBottom: "1px solid #eef2f7" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>
                                      {linkType}
                                    </Badge>
                                    <div style={{ minWidth: 0 }} className="flex-grow-1">
                                      <div className="fw-bold text-dark" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {getLinkLabel(link)}
                                      </div>
                                      {getLinkSubtitle(link) && getLinkSubtitle(link) !== getLinkLabel(link) && (
                                        <small className="text-muted" style={{ fontSize: 11, wordBreak: "break-all" }}>
                                          {getLinkSubtitle(link)}
                                        </small>
                                      )}
                                    </div>
                                    {isSuperAdmin && linkType === "Tenant" && rawId && (
                                      <Button
                                        variant="white"
                                        className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1 flex-shrink-0"
                                        style={{ fontSize: "12px", borderRadius: "6px", fontWeight: 700 }}
                                        disabled={loginBusy === `tenant:${rawId}`}
                                        onClick={() => handleLoginAsTenant(String(rawId))}
                                        title="Login as tenant"
                                      >
                                        <FiLogIn size={13} /> {loginBusy === `tenant:${rawId}` ? "..." : "Login"}
                                      </Button>
                                    )}
                                  </div>
                                  {linkType === "Tenant" && rawId && isSuperAdmin && (
                                    <div className="mt-1" style={{ marginLeft: 52 }}>
                                      {tenantEntry?.loading ? (
                                        <small className="text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: 11 }}>
                                          <Spinner size="sm" animation="border" style={{ width: 11, height: 11 }} /> Loading shared landlords…
                                        </small>
                                      ) : tenantEntry && tenantEntry.landlords.length > 0 ? (
                                        <div className="d-flex flex-column gap-1 mt-1">
                                          {tenantEntry.landlords.map((landlord) => (
                                            <div key={landlord.key} className="d-flex align-items-center gap-2">
                                              <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>
                                                Landlord
                                              </Badge>
                                              <div style={{ minWidth: 0 }} className="flex-grow-1">
                                                <div className="fw-bold text-dark" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                  {landlord.landlordName}
                                                </div>
                                                <small className="text-muted" style={{ fontSize: 11, wordBreak: "break-all" }}>
                                                  {landlord.email}
                                                  {landlord.workspaceName ? ` · ${landlord.workspaceName}` : ""}
                                                </small>
                                              </div>
                                              {landlord.landlordId && (
                                                <Button
                                                  variant="white"
                                                  className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1 flex-shrink-0"
                                                  style={{ fontSize: "12px", borderRadius: "6px", fontWeight: 700 }}
                                                  disabled={loginBusy === `landlord:${landlord.landlordId}`}
                                                  onClick={() => handleLoginAsLandlord(String(landlord.landlordId))}
                                                  title={`Login as ${landlord.landlordName}`}
                                                >
                                                  <FiLogIn size={13} /> {loginBusy === `landlord:${landlord.landlordId}` ? "..." : "Login"}
                                                </Button>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : tenantEntry ? (
                                        <small className="text-muted" style={{ fontSize: 11 }}>
                                          Not shared with any landlord.
                                        </small>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  );
                })()}
              </div>

              {/* Subject */}
              <div className="mb-2">
                <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                  Subject
                </p>
                <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4, color: "#1e293b" }}>
                  {detail.subject || "—"}
                </div>
              </div>

              {/* Message */}
              <div className="mb-3">
                <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                  Message
                </p>
                <div className="bg-light border rounded px-2 py-2" style={{ fontSize: 15, fontStyle: "italic", lineHeight: 1.6, color: "#334155", whiteSpace: "pre-wrap" }}>
                  {detail.message || "—"}
                </div>
              </div>

              {/* Documents */}
              {(detail.document_names?.length || 0) > 0 && (
                <div className="mb-3">
                  <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                    Linked documents
                  </p>
                  <div className="d-flex gap-1 flex-wrap">
                    {detail.document_names.map((d) => (
                      <Badge key={d} bg="light" text="dark" className="border">
                        {d}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div className="mb-3">
                <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                  Attachments ({detail.attachments?.length || 0})
                </p>
                {(detail.attachments?.length || 0) > 0 ? (
                  <div style={{ borderTop: "1px solid #eef2f7" }}>
                    {detail.attachments.map((a, i) => (
                      <a
                        key={`${a.file_url}-${i}`}
                        href={a.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="d-flex align-items-center gap-2 text-decoration-none py-2"
                        style={{ fontSize: 13, color: "#2563eb", borderBottom: "1px solid #eef2f7" }}
                      >
                        <FiPaperclip size={13} className="text-muted" />
                        <span className="fw-bold">{a.file_name || `File ${i + 1}`}</span>
                        {a.mime_type && (
                          <small className="text-muted ms-auto">{a.mime_type}</small>
                        )}
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">No attachments.</div>
                )}
              </div>

              {/* Ops: status + urgency */}
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Status
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-6">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Urgency
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={editUrgency}
                    onChange={(e) => setEditUrgency(e.target.value)}
                  >
                    {URGENCY_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4">
          <Button variant="light" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button
            style={{ background: "#253837", border: "none" }}
            onClick={handleSaveStatus}
            disabled={saving || detailLoading || !detail || (editStatus === detail?.status && editUrgency === detail?.urgency)}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .skor-filter-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          align-items: end;
        }
        .skor-filter-row .llm-filter-item {
          min-width: 0;
        }
        .skor-filter-row .skor-filter-search,
        .skor-filter-row .skor-span-3 {
          grid-column: 1 / -1;
        }
        .skor-filter-row .llm-btn-apply {
          justify-self: start;
          align-self: end;
        }
        .skor-filter-row .form-switch {
          padding-left: 52px;
          margin-bottom: 0;
          min-height: 38px;
        }
        .skor-filter-row .form-switch .form-check-input {
          width: 44px;
          height: 24px;
          margin-left: -52px;
          margin-top: 0;
          cursor: pointer;
        }
        @media (min-width: 640px) {
          .skor-filter-row {
            grid-template-columns: repeat(6, 1fr);
          }
          .skor-filter-row .skor-span-2 {
            grid-column: span 2;
          }
          .skor-filter-row .skor-span-3 {
            grid-column: span 3;
          }
        }
        @media (min-width: 1100px) {
          .skor-filter-row {
            grid-template-columns: repeat(12, 1fr);
          }
          .skor-filter-row .skor-filter-search {
            grid-column: span 4;
          }
        }
        .pagination .page-item .page-link {
          color: var(--llm-color-text-secondary);
          border: 1px solid var(--llm-color-border);
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .pagination .page-item.active .page-link {
          background-color: var(--llm-color-primary) !important;
          border-color: var(--llm-color-primary) !important;
          color: var(--llm-color-dark) !important;
        }
      `}</style>
    </div>
  );
};

export default SkorTickets;
